const baseUrl =
	(import.meta.env.VITE_PAYMENTS_API_BASE_URL as string | undefined) ||
	(import.meta.env.VITE_API_BASE_URL as string | undefined) ||
	'https://3xdxnpvvoe.execute-api.ap-southeast-2.amazonaws.com/dev';

export type CreateCheckoutParams =
	| { productId: 'starter' | 'pro' | 'byok' }
	| { productId: 'topup'; quantity: number };

export type CreateCheckoutResponse = {
	url: string;
	sessionId?: string;
};

async function http<T>(path: string, options: RequestInit): Promise<T> {
	if (!baseUrl) {
		throw new Error('Payments API URL not configured (VITE_PAYMENTS_API_BASE_URL)');
	}
	const res = await fetch(`${baseUrl}${path}`, {
		...options,
		headers: {
			'Content-Type': 'application/json',
			...(options.headers || {})
		}
	});
	if (!res.ok) {
		const contentType = res.headers.get('content-type') || '';
		try {
			if (contentType.includes('application/json')) {
				const data = (await res.json()) as any;
				const msg =
					(typeof data?.error === 'string' && data.error) ||
					(typeof data?.message === 'string' && data.message) ||
					JSON.stringify(data);
				throw new Error(msg || `Request failed: ${res.status}`);
			} else {
				const text = await res.text();
				throw new Error(text || `Request failed: ${res.status}`);
			}
		} catch (e: any) {
			throw new Error(e?.message || `Request failed: ${res.status}`);
		}
	}
	return (await res.json()) as T;
}

export type ConfirmPaymentResponse = {
	success: boolean;
	credits: number;
};

export type SubscriptionResponse = {
	hasSubscription: boolean;
	productId?: string;
	planName?: string;
	status?: string;
};

export type PortalResponse = {
	url: string;
};

export const PaymentService = {
	/**
	 * Create a Stripe Checkout session and return the URL to redirect the user.
	 */
	createCheckout: (
		userId: string,
		params: CreateCheckoutParams,
		authHeader: Record<string, string>
	) =>
		http<CreateCheckoutResponse>(`/payments/${userId}/checkout`, {
			method: 'POST',
			body: JSON.stringify(params),
			headers: authHeader
		}),

	/**
	 * Confirm payment after redirect from Stripe with session_id.
	 * Backend retrieves the session, reads credits from product metadata, and adds credits.
	 */
	confirmPayment: (
		userId: string,
		sessionId: string,
		authHeader: Record<string, string>
	) =>
		http<ConfirmPaymentResponse>(`/payments/${userId}/confirm`, {
			method: 'POST',
			body: JSON.stringify({ session_id: sessionId }),
			headers: authHeader
		}),

	/** Get current active subscription for display on pricing and account pages. */
	getSubscription: (userId: string, authHeader: Record<string, string>) =>
		http<SubscriptionResponse>(`/payments/${userId}/subscription`, {
			method: 'GET',
			headers: authHeader
		}),

	/** Get Stripe Customer Portal URL to manage/cancel/change subscription. */
	getPortalUrl: (userId: string, authHeader: Record<string, string>) =>
		http<PortalResponse>(`/payments/${userId}/portal`, {
			method: 'POST',
			body: JSON.stringify({}),
			headers: authHeader
		})
};
